import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { formatDate } from "../lib/format";

export function CoverImage({ src, alt, index = 0 }) {
  const [failed, setFailed] = useState(false);
  if (src && !failed)
    return (
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onError={() => setFailed(true)}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
      />
    );
  return (
    <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-surface to-neutral-50 noise-overlay flex items-end p-6">
      <span className="font-playfair italic text-indigo-900/80 text-5xl leading-none">
        {String(index + 1).padStart(2, "0")}
      </span>
    </div>
  );
}

export function ArticleMeta({ article }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-neutral-500" data-testid="article-meta">
      <span className="font-medium text-neutral-700">By {article.author}</span>
      <span aria-hidden="true">·</span>
      <time dateTime={article.publication_date}>{formatDate(article.publication_date)}</time>
      <span aria-hidden="true">·</span>
      <span>{article.reading_time} min read</span>
    </div>
  );
}

export function CategoryTag({ children }) {
  return (
    <span className="absolute top-4 left-4 px-2.5 py-1 rounded-full bg-surface/90 backdrop-blur-md text-[10px] font-semibold tracking-[0.18em] uppercase text-indigo-900 shadow-sm">
      {children}
    </span>
  );
}

export default function ArticleCard({ article, basePath, index = 0 }) {
  const path = `${basePath}/${article.slug}`;
  return (
    <Link
      to={path}
      data-testid={`article-card-${article.slug}`}
      className="group flex flex-col bg-surface border border-neutral-200 rounded-xl overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-neutral-100">
        <CoverImage src={article.image_url} alt={article.title} index={index} />
        <CategoryTag>{article.category}</CategoryTag>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <span className="eyebrow text-neutral-500 mb-3">{article.type === "review" ? "Review" : "Guide"}</span>
        <h3 className="font-playfair text-xl text-neutral-900 leading-snug mb-3 group-hover:text-indigo-900 transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-neutral-600 leading-relaxed line-clamp-3 mb-6">
          {article.excerpt}
        </p>
        <div className="mt-auto pt-4 border-t border-neutral-100 flex items-end justify-between gap-4">
          <ArticleMeta article={article} />
          <ArrowRight size={16} className="shrink-0 text-indigo-800 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}
